import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type TourHighlightContextValue = {
  isTourActive: boolean;
  setIsTourActive: React.Dispatch<React.SetStateAction<boolean>>;
  activeDotId: string | null;
  setActiveDotId: (id: string | null) => void;
  dismissedDotIds: Set<string>;
  dismissDotId: (id: string) => void;
  resetDismissedDotIds: () => void;
};

type TourHighlightProviderProps = {
  children: React.ReactNode;
  isTourActive?: boolean;
  setIsTourActive?: React.Dispatch<React.SetStateAction<boolean>>;
};

const TourHighlightContext = createContext<TourHighlightContextValue | undefined>(undefined);

export const TourHighlightProvider = ({
  children,
  isTourActive: controlledIsTourActive,
  setIsTourActive: controlledSetIsTourActive,
}: TourHighlightProviderProps) => {
  const [uncontrolledIsTourActive, setUncontrolledIsTourActive] = useState(false);
  const isTourActive = controlledIsTourActive ?? uncontrolledIsTourActive;
  const setIsTourActive = controlledSetIsTourActive ?? setUncontrolledIsTourActive;

  const [activeDotId, setActiveDotId] = useState<string | null>(null);
  const [dismissedDotIds, setDismissedDotIds] = useState<Set<string>>(new Set());

  const prevIsTourActiveRef = useRef(isTourActive);

  useEffect(() => {
    if (!isTourActive) {
      setActiveDotId(null);
    }

    if (isTourActive && !prevIsTourActiveRef.current) {
      setDismissedDotIds(new Set());
    }

    prevIsTourActiveRef.current = isTourActive;
  }, [isTourActive]);

  const dismissDotId = useCallback((id: string) => {
    setDismissedDotIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const resetDismissedDotIds = useCallback(() => {
    setDismissedDotIds(new Set());
  }, []);

  const value = useMemo(
    () => ({
      isTourActive,
      setIsTourActive,
      activeDotId,
      setActiveDotId,
      dismissedDotIds,
      dismissDotId,
      resetDismissedDotIds,
    }),
    [isTourActive, setIsTourActive, activeDotId, dismissedDotIds, dismissDotId, resetDismissedDotIds]
  );

  return <TourHighlightContext.Provider value={value}>{children}</TourHighlightContext.Provider>;
};

export const useTourHighlight = () => {
  const context = useContext(TourHighlightContext);
  if (!context) {
    throw new Error('useTourHighlight must be used within TourHighlightProvider');
  }
  return context;
};
